port module Main exposing (..)

import Browser
import Dict exposing (Dict)
import Html exposing (..)
import Html.Attributes exposing (..)
import Html.Events exposing (..)
import Http
import Json.Decode as JD
import Json.Encode as JE
import QRCode
import RemoteData as RD exposing (RemoteData)
import Svg.Attributes as SvgA
import Task
import Time



--


main : Program Flags Model Msg
main =
    Browser.element
        { init = init
        , update = update
        , subscriptions = subscriptions
        , view = view
        }



-- MODEL


type ApiError
    = ClientError Int String
    | ServerError Int String
    | DecodeError String
    | NetworkError
    | Timeout
    | BadUrl String


type alias ApiData a =
    RemoteData ApiError a


type alias Flags =
    { backendUrl : String, serviceUrl : String }


type alias Config =
    { backendUrl : String, serviceUrl : String }


type alias Model =
    { currentTime : Time.Posix
    , zone : Time.Zone
    , userInfo : ApiData UserInfo
    , config : Config
    , qrSession : Maybe QrSession
    , inputOtp : Maybe String
    , otpLoginResponse : ApiData ()
    , logoutResponse : ApiData ()
    , showLoading : Bool
    , gcCodeInputComponents : Dict Int GcCodeInputComponent
    , sampleDropOffResponse : ApiData DroppedSamplesResponse
    }


type alias UserInfo =
    { name : String }


type alias QrSession =
    { sessionId : String
    , expiresAt : Time.Posix
    }


type alias GcCodeInputComponent =
    { isRange : Bool
    , gcCodeInputCompound : Maybe GcCodeInputCompound
    }


type alias GcCodeInputCompound =
    { start : GcCodeInput
    , end : Maybe GcCodeInput
    }


type alias GcCodeInput =
    { gccode : String
    , isValid : Bool
    , errorMessage : Maybe String
    }


type alias DroppedSamplesResponse =
    { success : Maybe String
    , error : Maybe String
    }


init : Flags -> ( Model, Cmd Msg )
init flags =
    let
        config =
            { backendUrl = flags.backendUrl, serviceUrl = flags.serviceUrl }
    in
    ( { currentTime = Time.millisToPosix 0
      , zone = Time.utc
      , userInfo = RD.NotAsked
      , config = config
      , qrSession = Nothing
      , inputOtp = Nothing
      , otpLoginResponse = RD.NotAsked
      , logoutResponse = RD.NotAsked
      , showLoading = False
      , gcCodeInputComponents = Dict.empty
      , sampleDropOffResponse = RD.NotAsked
      }
    , Cmd.batch
        [ Task.perform AdjustTimeZone Time.here
        , Task.perform Tick Time.now
        ]
    )


getUserInfo : Config -> Cmd Msg
getUserInfo config =
    Http.riskyRequest
        { method = "get"
        , headers = []
        , url = config.serviceUrl ++ "/user/info"
        , body = Http.stringBody "application/json" ""
        , expect = expectJsonWithErrorBody GotUserInfo userInfoDecoder
        , timeout = Nothing
        , tracker = Nothing
        }


userInfoDecoder : JD.Decoder UserInfo
userInfoDecoder =
    JD.map UserInfo (JD.at [ "email" ] JD.string)


sampledropLogin : Config -> String -> QrSession -> Cmd Msg
sampledropLogin config otp qrSession =
    let
        requestBody =
            [ ( "otp", JE.string otp )
            , ( "qr_session_id", JE.string qrSession.sessionId )
            ]
                |> JE.object
                |> Http.jsonBody
    in
    Http.riskyRequest
        { method = "post"
        , headers = []
        , url = config.serviceUrl ++ "/login/sampledrop"
        , body = requestBody
        , expect = expectWhateverWithErrorBody OtpLoginResponse
        , timeout = Nothing
        , tracker = Nothing
        }


sampledropLogout : Config -> Cmd Msg
sampledropLogout config =
    Http.riskyRequest
        { method = "post"
        , headers = []
        , url = config.serviceUrl ++ "/logout/sampledrop"
        , body = Http.stringBody "application/json" ""
        , expect = expectWhateverWithErrorBody GotLogoutResponse
        , timeout = Nothing
        , tracker = Nothing
        }


sampledropper : Config -> List GcCodeInput -> Cmd Msg
sampledropper config samples =
    let
        rawSamples =
            samples |> List.filter .isValid |> List.map .gccode

        requestBody =
            JE.object
                [ ( "samples", JE.list JE.string rawSamples )
                ]
    in
    Http.riskyRequest
        { method = "post"
        , headers = []
        , url = config.serviceUrl ++ "/sampledropper"
        , body = Http.stringBody "application/json" (JE.encode 0 requestBody)
        , expect = expectJsonWithErrorBody GotDroppedSamplesResponse droppedSamplesResponse
        , timeout = Nothing
        , tracker = Nothing
        }


droppedSamplesResponse : JD.Decoder DroppedSamplesResponse
droppedSamplesResponse =
    JD.map2 DroppedSamplesResponse
        (JD.maybe (JD.field "success" JD.string))
        (JD.maybe (JD.field "error" JD.string))


expectJsonWithErrorBody : (ApiData a -> msg) -> JD.Decoder a -> Http.Expect msg
expectJsonWithErrorBody toMsg decoder =
    let
        resolve response =
            case response of
                Http.GoodStatus_ _ body ->
                    case JD.decodeString decoder body of
                        Ok value ->
                            Ok value

                        Err err ->
                            Err (DecodeError (JD.errorToString err))

                Http.BadStatus_ metadata body ->
                    let
                        status =
                            metadata.statusCode
                    in
                    if status >= 500 then
                        Err (ServerError status body)

                    else
                        Err (ClientError status body)

                Http.BadUrl_ url ->
                    Err (BadUrl url)

                Http.Timeout_ ->
                    Err Timeout

                Http.NetworkError_ ->
                    Err NetworkError

        resultToRemoteData result =
            case result of
                Ok value ->
                    RD.Success value

                Err error ->
                    RD.Failure error
    in
    Http.expectStringResponse (resultToRemoteData >> toMsg) resolve


expectWhateverWithErrorBody : (ApiData () -> msg) -> Http.Expect msg
expectWhateverWithErrorBody toMsg =
    let
        resolve response =
            case response of
                Http.GoodStatus_ _ _ ->
                    Ok ()

                Http.BadStatus_ metadata body ->
                    let
                        status =
                            metadata.statusCode
                    in
                    if status >= 500 then
                        Err (ServerError status body)

                    else
                        Err (ClientError status body)

                Http.BadUrl_ url ->
                    Err (BadUrl url)

                Http.Timeout_ ->
                    Err Timeout

                Http.NetworkError_ ->
                    Err NetworkError

        resultToRemoteData result =
            case result of
                Ok value ->
                    RD.Success value

                Err error ->
                    RD.Failure error
    in
    Http.expectStringResponse (resultToRemoteData >> toMsg) resolve



-- UPDATE


type Msg
    = Tick Time.Posix
    | AdjustTimeZone Time.Zone
    | RefreshPage
    | RequestSecureUuid
    | ReceivedSecureUuid String
    | LogOut
    | GotLogoutResponse (ApiData ())
    | GotUserInfo (ApiData UserInfo)
    | GotOtpInput String
    | LoginWithOtp String QrSession
    | OtpLoginResponse (ApiData ())
    | NoOp
    | ReceivedCurrentUrl String
    | ReInit
    | SetLoading Bool
    | UpdateNumberOfGcCodeInputComponents Int
    | UpdateInputComponentState ( Int, GcCodeInputComponent )
    | DropSamplesOff
    | GotDroppedSamplesResponse (ApiData DroppedSamplesResponse)


reInitModel : Model -> Model
reInitModel model =
    { model
        | userInfo = RD.NotAsked
        , qrSession = Nothing
        , inputOtp = Nothing
        , showLoading = False
        , sampleDropOffResponse = RD.NotAsked
        , gcCodeInputComponents = Dict.empty
        , logoutResponse = RD.NotAsked
    }


initialGcCodeInputComponent : GcCodeInputComponent
initialGcCodeInputComponent =
    { isRange = False
    , gcCodeInputCompound = Nothing
    }


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case msg of
        NoOp ->
            ( model, Cmd.none )

        Tick newTime ->
            let
                updatedModel =
                    case model.qrSession of
                        Just session ->
                            if Time.posixToMillis newTime >= Time.posixToMillis session.expiresAt then
                                reInitModel model

                            else
                                { model | qrSession = Just session }

                        Nothing ->
                            { model | qrSession = Nothing }
            in
            ( { updatedModel | currentTime = newTime }, Cmd.none )

        AdjustTimeZone newZone ->
            ( { model | zone = newZone }, Cmd.none )

        RefreshPage ->
            ( model, refreshPage () )

        RequestSecureUuid ->
            ( model, requestSecureUuid () )

        ReceivedSecureUuid newUuid ->
            let
                sessionId =
                    "qr_" ++ newUuid

                expiresAt =
                    Time.millisToPosix (Time.posixToMillis model.currentTime + (15 * 60 * 1000))

                newSession =
                    { sessionId = sessionId, expiresAt = expiresAt }
            in
            ( { model | qrSession = Just newSession, inputOtp = Nothing }, Cmd.none )

        GotOtpInput userInput ->
            let
                numericOnly =
                    String.filter Char.isDigit userInput

                limitedLength =
                    String.left 6 numericOnly
            in
            ( { model | inputOtp = Just limitedLength }, Cmd.none )

        LoginWithOtp submittedOtp sessionId ->
            ( { model | otpLoginResponse = RD.Loading }, sampledropLogin model.config submittedOtp sessionId )

        OtpLoginResponse response ->
            let
                updatedModel =
                    { model | otpLoginResponse = response }
            in
            case response of
                RD.Success _ ->
                    ( updatedModel, getUserInfo model.config )

                _ ->
                    ( updatedModel, Cmd.none )

        GotUserInfo result ->
            let
                updatedModel =
                    { model | userInfo = result }
            in
            case result of
                RD.Failure _ ->
                    ( updatedModel, getCurrentUrl () )

                _ ->
                    ( updatedModel, Cmd.none )

        LogOut ->
            ( { model | logoutResponse = RD.Loading }, sampledropLogout model.config )

        GotLogoutResponse response ->
            case response of
                RD.Success () ->
                    ( reInitModel model, getCurrentUrl () )

                _ ->
                    ( { model | logoutResponse = response }, Cmd.none )

        ReceivedCurrentUrl currentUrl ->
            let
                loginUrl : String
                loginUrl =
                    "https://sampledrop.genomicscore.be"
            in
            ( model, redirectToLogin loginUrl )

        ReInit ->
            ( reInitModel model, Cmd.none )

        SetLoading isLoading ->
            ( { model | showLoading = isLoading }, Cmd.none )

        UpdateNumberOfGcCodeInputComponents numOfInputs ->
            let
                cleanNumOfInputs =
                    clamp 1 25 numOfInputs

                currentNumOfComponents =
                    model.gcCodeInputComponents |> Dict.size

                updatedComponents =
                    if currentNumOfComponents > cleanNumOfInputs then
                        model.gcCodeInputComponents
                            |> Dict.keys
                            |> List.filter (\key -> key > cleanNumOfInputs)
                            |> List.foldl (\key acc -> Dict.remove key acc) model.gcCodeInputComponents

                    else if currentNumOfComponents < cleanNumOfInputs then
                        List.range (currentNumOfComponents + 1) cleanNumOfInputs
                            |> List.foldl (\key acc -> Dict.insert key initialGcCodeInputComponent acc) model.gcCodeInputComponents

                    else
                        model.gcCodeInputComponents
            in
            ( { model | gcCodeInputComponents = updatedComponents }, Cmd.none )

        UpdateInputComponentState ( id, updatedComponent ) ->
            let
                updatedComponents =
                    model.gcCodeInputComponents |> Dict.insert id updatedComponent
            in
            ( { model | gcCodeInputComponents = updatedComponents }, Cmd.none )

        DropSamplesOff ->
            if model.gcCodeInputComponents |> Dict.isEmpty then
                ( model, Cmd.none )

            else
                let
                    allSamples =
                        model.gcCodeInputComponents
                            |> Dict.values
                            |> List.map (\component -> fromComponentToGcCodes component)
                            |> List.concat
                in
                ( { model | sampleDropOffResponse = RD.Loading }, sampledropper model.config allSamples )

        GotDroppedSamplesResponse response ->
            ( { model | sampleDropOffResponse = response }, Cmd.none )


apiErrorToString : ApiError -> String
apiErrorToString error =
    case error of
        ClientError status body ->
            "Client error (" ++ String.fromInt status ++ "): " ++ body

        ServerError status body ->
            "Server error (" ++ String.fromInt status ++ "): " ++ body ++ " If this persists, please contact us through the helpdesk (https://help.genomicscore.be/helpdesk)"

        DecodeError err ->
            "Decode error: " ++ err

        NetworkError ->
            "Network error - please check your connection"

        Timeout ->
            "Request timed out"

        BadUrl url ->
            "Invalid URL: " ++ url


isSampleDropper : List String -> Bool
isSampleDropper roles =
    List.member "sample_dropper" roles


createValidatedGcCodeInput : GcCodeInput -> GcCodeInput
createValidatedGcCodeInput gcCode =
    let
        isValid =
            validateGcCode gcCode.gccode
    in
    { gcCode
        | isValid = isValid |> Tuple.first
        , errorMessage = isValid |> Tuple.second
    }


validateGcCode : String -> ( Bool, Maybe String )
validateGcCode gcCode =
    let
        digitPart =
            String.dropLeft 2 gcCode
    in
    if not (String.left 2 gcCode == "GC") then
        ( False, Just "GC code must start with GC" )

    else if not (String.length digitPart == 6) then
        ( False, Just "GC code must be 6 digits long. No need to add additional suffixes such as plate ids or indices" )

    else if not (String.all Char.isDigit digitPart) then
        ( False, Just "GC code must contain only digits" )

    else
        ( True, Nothing )


fromComponentToGcCodes : GcCodeInputComponent -> List GcCodeInput
fromComponentToGcCodes inputComponent =
    case inputComponent.gcCodeInputCompound of
        Just compound ->
            if inputComponent.isRange then
                case compound.end of
                    Just end ->
                        let
                            s =
                                compound.start.gccode |> String.dropLeft 2 |> String.toInt |> Maybe.withDefault 0

                            f =
                                end.gccode |> String.dropLeft 2 |> String.toInt |> Maybe.withDefault 0
                        in
                        List.range s f
                            |> List.map
                                (\i ->
                                    { gccode = "GC" ++ String.fromInt i
                                    , isValid = True
                                    , errorMessage = Nothing
                                    }
                                )

                    Nothing ->
                        [ compound.start ]

            else
                [ compound.start ]

        Nothing ->
            []


commonStyle : List (Attribute msg)
commonStyle =
    [ style "margin-top" "10px", style "color" "#666" ]


viewSessionExpiryFromModel : Model -> Html Msg
viewSessionExpiryFromModel model =
    case model.qrSession of
        Just qrSession ->
            viewSessionExpiry qrSession.expiresAt model.currentTime

        Nothing ->
            p [] [ text "😑 error!" ]


viewSessionExpiry : Time.Posix -> Time.Posix -> Html Msg
viewSessionExpiry qrSessionExpiry currentTime =
    p commonStyle
        [ text ("Session expires in: " ++ displayRemainingTime qrSessionExpiry currentTime) ]


viewApiError : ApiError -> Html Msg
viewApiError error =
    div [ style "margin" "2rem" ] [ text (apiErrorToString error) ]


viewBeforeLogin : Model -> Html Msg
viewBeforeLogin model =
    let
        stepOne =
            p commonStyle
                [ text "Click Start to generate a QR code session" ]

        stepTwo =
            [ "(1) Scan the QR code with your phone", "(2) Login with your cumulus/share account", "(3) Enter the provided token below and Submit" ]
                |> List.map (\val -> p (commonStyle ++ [ style "margin" "0 0 2px 0" ]) [ text val ])
                |> p []
    in
    case model.qrSession of
        Nothing ->
            div []
                [ stepOne
                , input
                    [ type_ "button"
                    , onClick RequestSecureUuid
                    , value "Start"
                    ]
                    []
                ]

        Just qrSession ->
            div []
                [ stepTwo
                , hr [] []
                , viewSessionExpiry qrSession.expiresAt model.currentTime
                , qrCodeView (generateQrContent qrSession.sessionId) qrSession.sessionId
                , hr [] []
                , viewOtpInput model qrSession
                , case model.otpLoginResponse of
                    RD.Failure error ->
                        viewApiError error

                    RD.Loading ->
                        viewLoading (Just "Signing in...")

                    RD.Success _ ->
                        text ""

                    RD.NotAsked ->
                        text ""
                ]


viewAfterLogin : Model -> Html Msg
viewAfterLogin model =
    let
        isInvalid =
            if Dict.isEmpty model.gcCodeInputComponents then
                True

            else
                model.gcCodeInputComponents
                    |> Dict.values
                    |> List.map (\comp -> comp.gcCodeInputCompound |> Maybe.withDefault { start = { isValid = True, gccode = "", errorMessage = Nothing }, end = Nothing })
                    |> List.all
                        (\compound ->
                            let
                                isEndValid =
                                    case compound.end of
                                        Just end ->
                                            end.isValid

                                        Nothing ->
                                            True
                            in
                            compound.start.isValid && isEndValid
                        )
                    |> not
    in
    div []
        [ viewSessionExpiryFromModel model
        , div [ style "margin-top" "2.5rem" ]
            [ div [ class "grid" ]
                [ p [ style "display" "flex", style "align-items" "center" ] [ text "Number of GC codes" ]
                , input
                    [ type_ "number"
                    , name "number"
                    , placeholder "Number of GC codes"
                    , attribute "aria-label" "Number of GC codes"
                    , style "text-align" "center"
                    , attribute "inputmode" "numeric"
                    , attribute "min" "1"
                    , attribute "max" "25"
                    , value (model.gcCodeInputComponents |> Dict.size |> String.fromInt)
                    , step "1"
                    , onInput (\inputValue -> inputValue |> String.toInt |> Maybe.withDefault 1 |> UpdateNumberOfGcCodeInputComponents)
                    ]
                    []
                ]
            , model.gcCodeInputComponents
                |> Dict.toList
                |> List.map (\( idx, comp ) -> viewGcCodeInputComponent comp idx)
                |> div []
            ]
        , div [ attribute "role" "group" ]
            [ button [ class "primary", onClick DropSamplesOff, disabled isInvalid ] [ text "Submit" ]
            , button [ class "outline primary", onClick LogOut ] [ text "Exit" ]
            ]
        , div []
            [ case model.sampleDropOffResponse of
                RD.NotAsked ->
                    text ""

                RD.Loading ->
                    viewLoading (Just "Submitting...")

                RD.Success successPayload ->
                    viewDropOffResponse successPayload

                RD.Failure (ClientError status body) ->
                    JD.decodeString droppedSamplesResponse body
                        |> Result.map viewDropOffResponse
                        |> Result.withDefault (text ("Client error (" ++ String.fromInt status ++ "): " ++ body))

                RD.Failure error ->
                    viewApiError error
            ]
        , div []
            [ case model.logoutResponse of
                RD.Loading ->
                    viewLoading (Just "Logging out...")

                RD.Failure error ->
                    viewApiError error

                _ ->
                    text ""
            ]
        ]


viewDropOffResponse : DroppedSamplesResponse -> Html Msg
viewDropOffResponse resp =
    let
        successView =
            resp.success
                |> Maybe.map (\s -> "✅ Succeeded: " ++ s)

        errorView =
            resp.error
                |> Maybe.map (\e -> "🫠 Failed: " ++ e)

        -- Combine *all* messages that exist
        allMessages =
            [ successView, errorView ]
                |> List.filterMap Basics.identity
                |> List.map (\v -> div [ attribute "display" "block" ] [ text v ])
                |> div []
    in
    allMessages



--


viewGcCodeInputComponent : GcCodeInputComponent -> Int -> Html Msg
viewGcCodeInputComponent inputComponent inputID =
    let
        initVal =
            { gccode = "", isValid = False, errorMessage = Nothing }

        initCompound =
            { start = initVal, end = Nothing }

        compound =
            inputComponent.gcCodeInputCompound
                |> Maybe.withDefault initCompound

        viewInput =
            viewSingleGcCodeInput inputComponent compound

        startInput =
            viewInput compound.start inputID False

        endInput =
            if inputComponent.isRange then
                viewInput (compound.end |> Maybe.withDefault initVal) inputID True

            else
                text ""

        componentID =
            "is-range-" ++ String.fromInt inputID
    in
    div []
        [ div [ class "grid" ]
            [ div [ style "display" "flex", style "align-items" "center" ]
                [ label []
                    [ input
                        [ type_ "checkbox"
                        , attribute "role" "switch"
                        , id componentID
                        , name "is range"
                        , onCheck (\_ -> UpdateInputComponentState ( inputID, { inputComponent | isRange = not inputComponent.isRange } ))
                        ]
                        []
                    , text "Range"
                    ]
                ]
            , startInput
            , endInput
            ]
        ]


viewSingleGcCodeInput : GcCodeInputComponent -> GcCodeInputCompound -> GcCodeInput -> Int -> Bool -> Html Msg
viewSingleGcCodeInput inputComponent inputCompound currentInputValue inputID isEnd =
    let
        componentID =
            "error-message-" ++ String.fromInt inputID

        errorMessages =
            currentInputValue.errorMessage |> Maybe.withDefault ""

        isPresent =
            currentInputValue.gccode /= ""

        isInvalid =
            if String.length errorMessages > 0 then
                "true"

            else
                "false"

        updateInputValue : String -> GcCodeInput
        updateInputValue gcCodeInput =
            let
                updatedGcCode =
                    { currentInputValue | gccode = gcCodeInput |> String.toUpper }
            in
            createValidatedGcCodeInput updatedGcCode

        updateCompoundValue : GcCodeInput -> GcCodeInputCompound
        updateCompoundValue updatedInputValue =
            if isEnd then
                { inputCompound | end = Just updatedInputValue }

            else
                { inputCompound | start = updatedInputValue }

        updateComponent : GcCodeInputCompound -> GcCodeInputComponent
        updateComponent updatedCompound =
            { inputComponent | gcCodeInputCompound = Just updatedCompound }

        updateComponentFromGcCode : String -> GcCodeInputComponent
        updateComponentFromGcCode =
            updateComponent << updateCompoundValue << updateInputValue

        baseInputAttrs : List (Attribute Msg)
        baseInputAttrs =
            [ placeholder "GC code"
            , value currentInputValue.gccode
            , onInput (\gcCode -> UpdateInputComponentState ( inputID, updateComponentFromGcCode gcCode ))
            , style "font-weight" "bold"
            , style "font-family" "ui-monospace"
            , style "text-align" "center"
            , type_ "text"
            , maxlength 8
            , attribute "aria-describedby" componentID
            ]

        inputAttrs =
            if isPresent then
                baseInputAttrs ++ [ attribute "aria-invalid" isInvalid ]

            else
                baseInputAttrs
    in
    div []
        [ input inputAttrs []
        , small
            [ id componentID ]
            [ text errorMessages ]
        ]


view : Model -> Html Msg
view model =
    div []
        [ h3 [] [ text "GC Sample Drop" ]
        , hr [] []
        , case model.userInfo of
            RD.Success _ ->
                viewAfterLogin model

            _ ->
                viewBeforeLogin model
        ]


viewLoading : Maybe String -> Html Msg
viewLoading maybeSomeValueToShow =
    let
        loadingAttr =
            attribute "aria-busy" "true"
    in
    case maybeSomeValueToShow of
        Just someValue ->
            div [ loadingAttr ] [ text someValue ]

        Nothing ->
            div [ loadingAttr ] []


formatNumber : String -> String
formatNumber raw =
    if String.length raw > 3 then
        let
            part1 =
                String.slice 0 3 raw

            part2 =
                String.slice 3 6 raw
        in
        part1 ++ " " ++ part2

    else
        raw


viewOtpInput : Model -> QrSession -> Html Msg
viewOtpInput model qrSession =
    let
        currentValue =
            model.inputOtp |> Maybe.withDefault ""

        loginWithOtpCmd =
            case model.inputOtp of
                Just otp ->
                    let
                        sanitisedOtp =
                            otp
                                |> String.trim
                                |> String.replace " " ""
                    in
                    onClick (LoginWithOtp sanitisedOtp qrSession)

                Nothing ->
                    disabled True
    in
    div []
        [ input
            [ placeholder "Enter Token"
            , value (formatNumber currentValue)
            , onInput GotOtpInput
            , type_ "text"
            , attribute "inputmode" "numeric"
            , maxlength 7
            , style "font-weight" "bold"
            , style "font-family" "ui-monospace"
            , style "text-align" "center"
            , style "font-size" "28px"
            ]
            []
        , div [ attribute "role" "group" ]
            [ button [ class "primary", loginWithOtpCmd ] [ text "Enter" ]
            , button [ class "outline primary", onClick RequestSecureUuid ] [ text "Reset" ]
            , button [ class "outline primary", onClick ReInit ] [ text "Cancel" ]
            ]
        ]


displayRemainingTime : Time.Posix -> Time.Posix -> String
displayRemainingTime expiresAt currentTime =
    let
        remainingTime =
            Time.posixToMillis expiresAt - Time.posixToMillis currentTime

        remainingMinutes =
            remainingTime // 60000

        remainingSeconds =
            (remainingTime // 1000) |> modBy 60
    in
    String.fromInt remainingMinutes ++ ":" ++ String.padLeft 2 '0' (String.fromInt remainingSeconds)


formatTime : Time.Zone -> Time.Posix -> String
formatTime zone time =
    let
        hour =
            String.fromInt (Time.toHour zone time) |> String.padLeft 2 '0'

        minute =
            String.fromInt (Time.toMinute zone time) |> String.padLeft 2 '0'

        second =
            String.fromInt (Time.toSecond zone time) |> String.padLeft 2 '0'
    in
    hour ++ ":" ++ minute ++ ":" ++ second


generateSessionId : Time.Posix -> String
generateSessionId time =
    let
        timestamp =
            Time.posixToMillis time

        -- Create a simple random-like ID based on timestamp
        hash =
            String.fromInt (timestamp |> modBy 999999 |> (+) 100000)
    in
    "qr_" ++ hash


generateQrContent : String -> String
generateQrContent sessionId =
    "https://otp.genomicscore.be/qr/" ++ sessionId


qrCodeView : String -> String -> Html msg
qrCodeView message sessionId =
    div []
        [ QRCode.fromString message
            |> Result.map
                (QRCode.toSvg
                    [ SvgA.width "200px"
                    , SvgA.height "200px"
                    , style "background-color" "white"
                    ]
                )
            |> Result.withDefault (Html.text "Error while encoding to QRCode.")
        , p [ style "font-size" "0.7em", style "color" "#888", style "margin" "1em" ]
            [ text sessionId ]
        ]



-- SUBSCRIPTIONS


subscriptions : Model -> Sub Msg
subscriptions model =
    Sub.batch
        [ Time.every 1000 Tick
        , receiveCurrentUrl ReceivedCurrentUrl
        , receiveSecureUuid ReceivedSecureUuid
        ]



-- PORTS


port refreshPage : () -> Cmd msg


port receiveSocketMsg : (JD.Value -> msg) -> Sub msg


port sendSocketCommand : JE.Value -> Cmd msg


port redirectToLogin : String -> Cmd msg


port getCurrentUrl : () -> Cmd msg


port receiveCurrentUrl : (String -> msg) -> Sub msg


port requestSecureUuid : () -> Cmd msg


port receiveSecureUuid : (String -> msg) -> Sub msg
